import React, { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react';
import { User, AuthState, LoginCredentials } from '@/types/auth';
import { authApi } from '@/services/api/auth';
import { tokenStorage } from '@/services/storage/tokenStorage';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { notifyError } from '@/services/api/errors';

type SessionTokens = { accessToken: string | null; refreshToken: string | null };

// Типы действий
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User } & SessionTokens }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'REFRESH_TOKEN_SUCCESS'; payload: SessionTokens }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

// Начальное состояние
const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
};

// Reducer для управления состоянием аутентификации
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
      };

    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };

    case 'REFRESH_TOKEN_SUCCESS':
      return {
        ...state,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
      };

    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    default:
      return state;
  }
};

// Контекст аутентификации
interface AuthContextType {
  state: AuthState;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Провайдер аутентификации
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const queryClient = useQueryClient();

  // Восстановление сессии при загрузке приложения по сохранённым токенам.
  // Токены хранятся только в tokenStorage и пишутся туда явно (вход, обновление): прежняя синхронизация
  // «состояние → хранилище» записывала обратно токены, которые интерцептор API уже успел обновить
  useEffect(() => {
    const initializeAuth = async () => {
      if (!tokenStorage.hasTokens()) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      try {
        let user: User;
        try {
          user = await authApi.getProfile();
        } catch (profileError) {
          // Профиль не загрузился — обновляем токен и запрашиваем профиль ещё раз. Раньше после
          // успешного обновления профиль не перезапрашивался, и загрузка не заканчивалась никогда
          if (!(await refreshToken())) {
            throw profileError;
          }
          user = await authApi.getProfile();
        }
        // Токены — из хранилища: пока грузился профиль, интерцептор API мог их обновить
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, ...tokenStorage.getTokens() } });
      } catch (error) {
        // Сессию восстановить не удалось — экран входа с причиной вместо бесконечной загрузки
        tokenStorage.clearTokens();
        dispatch({ type: 'LOGOUT' });
        notifyError(error, 'Не удалось восстановить сессию. Войдите в систему снова');
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      
      const response = await authApi.login(credentials);
      tokenStorage.setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken });
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
      
      toast.success(`Добро пожаловать, ${response.user.firstName}!`);
    } catch (error: any) {
      dispatch({ type: 'LOGIN_FAILURE' });
      // Причину показывает форма входа рядом с полями — тост дублировал бы её
      throw error;
    }
  };

  const logout = () => {
    const { accessToken, refreshToken } = tokenStorage.getTokens();
    // Сессия на сервере завершается в фоне: выход не ждёт сети (AUTH-3)
    authApi.endSession(accessToken, refreshToken).catch(() => undefined);
    tokenStorage.clearTokens();
    // Данные прошлого пользователя не должны показаться следующему, кто войдёт в этом браузере
    queryClient.clear();
    dispatch({ type: 'LOGOUT' });
    toast.success('Вы успешно вышли из системы');
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      if (!tokenStorage.getRefreshToken()) {
        return false;
      }

      // Общее с интерцептором API обновление (одно на всех): новые токены оно само сохраняет в хранилище
      const response = await authApi.refreshToken();
      
      dispatch({
        type: 'REFRESH_TOKEN_SUCCESS',
        payload: response,
      });
      
      return true;
    } catch (error) {
      console.error('Ошибка обновления токена:', error);
      return false;
    }
  };

  const updateUser = (user: User) => {
    dispatch({ type: 'SET_USER', payload: user });
  };

  const contextValue: AuthContextType = {
    state,
    login,
    logout,
    refreshToken,
    updateUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Хук для использования контекста аутентификации
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  
  return context;
};
